import React, { useState, useEffect, useRef, useMemo } from "react"
import {
  XMarkIcon,
  Bars3Icon,
  PencilIcon,
  FolderIcon,
} from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import ContentEditor from "../content-editor/index"
import TypeResolver from "../type-resolver"
import DatabaseModelTypeResolver from "aldoo-ra/CMS/database-model-type-resolver"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import Controller from "../controllers/cms-controller"
import { useFieldDragController } from "../controllers/field-drag-controller"
import ContentSelectorDialog from "../dialogs/content-selector-dialog"
import { showNotification } from "aldoo-ra/Notification"
import { previewTitle } from "../preview"
import { isInternalType } from "../field-formatter"
import _ from "lodash"

// Normalize a value to an array — handles plain objects with numeric string keys
// (produced by the localization system, e.g. {"0": "a", "1": "b"})
// Also handles a single expanded CMS document (expand: true converts a string ObjectId
// to a full {_id, content} object — wrap it so array-based logic still works)
function toArray(val) {
  if (Array.isArray(val)) return val
  if (val && typeof val === "object") {
    const keys = Object.keys(val)
    if (keys.length > 0 && keys.every((k) => /^\d+$/.test(k))) {
      return keys.sort((a, b) => Number(a) - Number(b)).map((k) => val[k])
    }
    // Single CMS document or reference object — wrap in array
    if (val._id !== undefined) return [val]
  }
  return []
}

// Helper function for CMS content - applies instance values
const applyIValues = (targetObject, content) => {
  if (!content.ivalues || !Object.keys(content.ivalues).length || !targetObject)
    return
  const key = targetObject._id.toString()
  const values = content.ivalues[key]
  if (!values) return
  Object.assign(targetObject.content, values)
}

export default function ArrayEditor({
  onChange,
  value,
  field,
  parentValues,
  parentObject,
  showAddBtn = true,
  showClearBtn = true,
  showItemDelete = true,
}) {
  // Extract field type information
  const fieldType = field.type?.value || field.type

  // State - initialize all hooks first to maintain consistent order
  const [localValue, setLocalValue] = useState([])
  const [showDialog, setShowDialog] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [selectedInstance, setSelectedInstance] = useState(null)
  const [itemContentValues, setItemContentValues] = useState({})
  const [showContentDialog, setShowContentDialog] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  
  // Refs and controllers
  const inputRefs = useRef([])
  const isInternalChange = useRef(false)
  const loadingInstances = useRef(false)
  const isInitialized = useRef(false)
  const { getContentType } = TypeResolver()
  const { getType: getDatabaseModelType } = DatabaseModelTypeResolver()
  const controller = Controller() // Always initialize controller

  // Robust Classification Logic - Using Type Definitions (Memoized to prevent re-renders)
  const { isCMSContent, isEmbeddedType, isPrimitive } = useMemo(() => {
    const primitiveTypes = ['Text', 'String', 'Number', 'Boolean', 'Date']

    const extractReferenceType = () => {
      // Priority order for reference type detection:
      if (field.referenceType) return field.referenceType

      // For Content fields, check item field
      if (fieldType === "Content") {
        if (typeof field.item === 'string') return field.item
        if (field.item?.type) return field.item.type
      }

      // For Array fields, check various patterns
      if (fieldType === "Array") {
        if (field.itemType === "Reference" && field.referenceType) return field.referenceType
        // Don't treat primitive types as references
        if (typeof field.item === 'string' && !primitiveTypes.includes(field.item)) return field.item
        if (field.item?.type && !primitiveTypes.includes(field.item.type)) return field.item.type
        if (field.item?.value && !primitiveTypes.includes(field.item.value)) return field.item.value
      }

      return null
    }

    const referenceType = extractReferenceType()
    const cmsType = referenceType ? getContentType(referenceType) : null
    const dbModelType = referenceType ? getDatabaseModelType(referenceType) : null
    
    // Special case: Check if we're editing CMS system configuration arrays
    // These should be treated as embedded field definitions, not as separate content instances
    const parentValuesObj = parentValues ? parentValues() : {};
    const isEditingInternalType = parentValuesObj?.type && isInternalType({ type: parentValuesObj.type });
    
    // Most stable detection: Check if we're editing arrays of internal types by their item type
    const isEditingInternalTypeArray = (
      fieldType === "Array" &&
      field.item?.type &&
      isInternalType({ type: field.item.type })
    );
    
    const isCMSTypeConfigArray = (
      fieldType === "Array" && 
      (isEditingInternalType || isEditingInternalTypeArray)
    )
    
    
    // Classification based on field definition and type resolvers
    const isExplicitCMSContent = (
      fieldType === "Content" ||
      (fieldType === "Array" && field.itemType === "Reference") ||
      (fieldType === "Array" && referenceType && typeof referenceType === 'string')
    )
    
    const isExplicitEmbeddedContent = (
      field.item && 
      typeof field.item === 'object' && 
      field.item.fields && 
      Array.isArray(field.item.fields) &&
      fieldType !== "Content" &&
      !(fieldType === "Array" && field.itemType === "Reference")
    )
    
    // Check for primitive arrays (Array without item field or with primitive item types)
    const isPrimitiveArray = (
      fieldType === "Array" &&
      !field.itemType &&
      !referenceType &&
      (
        // No item field at all (e.g., Array of strings with options)
        !field.item ||
        // Item is a primitive type string
        (typeof field.item === 'string' && ['Text', 'String', 'Number', 'Boolean', 'Date'].includes(field.item)) ||
        // Has options field (typically indicates primitive values)
        field.options ||
        // Has fieldEditor but no complex item structure
        (field.fieldEditor && !field.item?.fields)
      )
    )

    
    // Final classification
    let isCMSContent = false
    let isEmbeddedType = false
    let isPrimitive = false
    
    if (isPrimitiveArray) {
      isPrimitive = true
      // Primitive arrays should use simpler handling - they don't need CMS loading logic
    } else if (isCMSTypeConfigArray) {
      // CMS type configuration arrays (fields, groups, editors) should be treated as embedded
      isEmbeddedType = true
    } else if (isExplicitCMSContent) {
      isCMSContent = true
    } else if (isExplicitEmbeddedContent) {
      isEmbeddedType = true
    } else {
      // Ambiguous field definition - check type resolvers as fallback
      if (referenceType && (cmsType || dbModelType)) {
        isCMSContent = true
      } else {
        isEmbeddedType = true // Safe default
      }
    }

    
    return { isCMSContent, isEmbeddedType, isPrimitive }
  }, [fieldType, field.id, field.itemType, field.referenceType, field.fieldEditor, field.options, field.item, parentValues])
  
  

  const labelField = field?.item?.labelField || "label"
  
  const isRequired =
    typeof field.required === "function"
      ? field.required(parentObject)
      : field.required

  // Unified function to update value and notify parent
  const updateValue = (newValue) => {
    if (isCMSContent) {
      setLocalValue(newValue)
    } else {
      // For embedded types and primitive arrays
      isInternalChange.current = true
      setLocalValue(newValue)
      onChange?.(newValue)
    }
  }

  // Initialize localValue based on content type and data - proper synchronous approach
  const valueRef = useRef()
  const lastUpdateRef = useRef(0)
  
  // Synchronous initialization - no timers
  useEffect(() => {
    // Use JSON comparison to check if value actually changed
    const valueString = JSON.stringify(value)
    const hasValueChanged = valueString !== valueRef.current
    
    // Prevent rapid consecutive updates
    const now = Date.now()
    if (now - lastUpdateRef.current < 50) return
    
    if (!hasValueChanged && isInitialized.current) return // No change, no update needed
    
    if (!isInitialized.current) {
      if (isCMSContent) {
        // For CMS content, initialize from the received value or empty
        const arr = toArray(value)
        if (arr.length > 0) {
          setLocalValue(arr)
        } else {
          setLocalValue([])
        }
      } else if (isEmbeddedType || isPrimitive) {
        // For embedded content and primitive arrays, initialize directly from value
        setLocalValue(toArray(value))
      } else {
        // Fallback - treat as CMS content if it has _id fields
        const arr = toArray(value)
        if (arr.length > 0 && arr[0]?._id) {
          setLocalValue(arr)
        } else {
          setLocalValue(arr)
        }
      }
      isInitialized.current = true
    } else if (hasValueChanged) {
      // Don't overwrite if localValue has loaded content and we're dealing with CMS content
      if (isCMSContent && localValue && localValue.length > 0) {
        const hasLoadedContent = localValue.some(item =>
          item.content && typeof item.content === 'object' && Object.keys(item.content).length > 1
        )
        if (hasLoadedContent) {
          valueRef.current = valueString
          lastUpdateRef.current = now
          return
        }
      }

      // Update only when external value actually changes (deep comparison)
      setLocalValue(toArray(value))
    }
    
    valueRef.current = valueString
    lastUpdateRef.current = now
  }, [value, isCMSContent, isEmbeddedType, isPrimitive])

  // Drag controller support
  const { handleDragStart, handleDragOver, handleDragEnd } =
    useFieldDragController(localValue, updateValue)

  // Simple CMS Content Loading - back to working version
  const loadItemInstances = async (data) => {
    if (!isCMSContent || loadingInstances.current || !controller) return
    
    setIsLoading(true)
    loadingInstances.current = true
    
    const loadedItems = await Promise.all(
      data.map(async (item) => {
        if (item._id) {
          try {
            const result = await controller.getContent({
              match: { _id: item._id },
            })
            
            if (result.data && result.data[0]) {
              applyIValues(result.data[0], parentValues())
              const content = result.data[0]?.content
              
              return {
                _id: item._id,
                itemLabel: content?.text || content?.name || content?.title || content?.label || previewTitle(result.data[0]) || item.label || "",
                ...result.data[0],
              }
            }
          } catch (error) {
            console.error('Failed to load content:', error)
          }
        }
        return {
          _id: item._id,
          itemLabel: item.label || "",
          ...item
        }
      })
    )
    
    setLocalValue(loadedItems)
    setIsLoading(false)
    loadingInstances.current = false
  }

  // Handle external value changes for embedded types and primitive arrays
  useEffect(() => {
    if ((isEmbeddedType || isPrimitive) && !isInternalChange.current && !_.isEqual(value, localValue)) {
      setLocalValue(value ?? [])
    }
    isInternalChange.current = false
  }, [value, isEmbeddedType, isPrimitive])

  // Simple CMS content loading - back to working version
  useEffect(() => {
    if (!isCMSContent) return

    // Normalize value: expand: true may return a single {_id, content} object instead of an array
    const valueArr = toArray(value)

    if (!valueArr.length) {
      if (fieldType === "Content" && !localValue) {
        setLocalValue([{ _id: null, itemLabel: "" }])
      }
      return
    }

    // Check if we already have the content loaded to avoid unnecessary re-loading
    const hasLoadedContent = localValue?.length > 0 && localValue?.some(item =>
      item.content && typeof item.content === 'object' && Object.keys(item.content).length > 1
    )

    if (hasLoadedContent && localValue.length === valueArr.length) return

    if (!localValue || localValue.length === 0 || localValue.length !== valueArr.length) {
      loadItemInstances(valueArr)
      return
    }
    loadItemInstances(valueArr)
  }, [value, field, isCMSContent, fieldType])

  // Update parent for CMS content
  useEffect(() => {
    if (!isCMSContent || !localValue) return

    // Don't run parent update until initialization is complete
    if (!isInitialized.current) return

    // Don't call onChange with empty array unless we had actual content before
    // This prevents clearing parent fields during re-renders and group toggles
    if (localValue.length === 0) {
      // NEVER clear fields during initialization or re-renders for CMS field arrays
      // This is causing the fields to disappear during group toggles
      return // Don't clear fields
    }

    const newValue = localValue.map((item) => {
      // Handle the case where we have expanded CMS content with metadata
      if (item.content && typeof item.content === 'object') {
        return {
          _id: item._id,
          label: item.content.name || item.content.title || item.content.label || item.content.id || item.itemLabel || ""
        }
      }

      // Handle simple CMS content structure
      return {
        _id: item._id,
        label: item.itemLabel || item.label || ""
      }
    })

    if (!newValue[0]?._id) return
    if (JSON.stringify(newValue) === JSON.stringify(value)) return

    onChange(newValue)
  }, [localValue, isCMSContent])

  // Helper functions
  const updateName = (label) => {
    if (!label) return ""
    return label
      .toLowerCase()
      .replace(/ /g, "_")
      .replace(/[^a-zA-Z0-9_]/g, "")
  }

  const autoResolveFields = (index) => {
    if (isCMSContent || field.item.autoID === false) return

    const updatedValues = [...localValue]
    const value = updatedValues[index]
    if (value[labelField] && !value.id) {
      value.id = updateName(value[labelField])
      updateValue(updatedValues)
    }
  }

  // Event handlers
  const handleValueChange = (updatedValues, index) => {
    const safeLocal = Array.isArray(localValue) ? localValue : toArray(localValue)
    try {
      if (isCMSContent) {
        // CMS content label change
        const updatedItems = [...safeLocal]
        if (updatedItems[index]) {
          updatedItems[index].itemLabel = updatedValues
          if (
            updatedItems[index].content &&
            Object.keys(updatedItems[index].content).includes("name")
          ) {
            updatedItems[index].content.name = updatedValues
          }
        }
        setLocalValue(updatedItems)
      } else if (isPrimitive) {
        // Primitive array value change - store values directly
        if (index === undefined) {
          updateValue([...safeLocal, updatedValues])
          return
        }

        const newValues = [...safeLocal]
        newValues[index] = updatedValues
        updateValue(newValues)
        inputRefs.current[index]?.focus()
      } else {
        // Embedded type value change
        if (index === undefined) {
          updateValue([
            ...safeLocal,
            {
              [labelField]: updatedValues,
            },
          ])
          return
        }

        const newValues = [...safeLocal]
        const currentItem = newValues[index] || {}
        
        // If this item has a content structure, update the content.text field
        if (currentItem.content && typeof currentItem.content === 'object') {
          newValues[index] = {
            ...currentItem,
            content: {
              ...currentItem.content,
              text: updatedValues  // Update the text in the content
            },
            [labelField]: updatedValues,  // Also update the label field
          }
        } else {
          // Simple structure
          newValues[index] = {
            ...currentItem,
            [labelField]: updatedValues,
          }
        }
        
        updateValue(newValues)
        inputRefs.current[index]?.focus()
      }
    } catch (error) {
      console.error("Error in handleValueChange:", error)
      showNotification({
        message: "Error updating value",
        className: "bg-admin_error text-white",
      })
    }
  }

  const addNewInput = () => {
    // Check if multiple is false and we already have an item
    if (field.multiple === false && localValue?.length > 0) {
      showNotification({
        message: "This field only accepts a single item",
        className: "bg-admin_warning text-white",
      })
      return
    }

    if (isCMSContent) {
      setLocalValue((prevValue) => {
        const newValue = [...(prevValue || []), { _id: null, itemLabel: "" }]
        setTimeout(() => {
          inputRefs.current[newValue.length - 1]?.focus()
        }, 10)
        return newValue
      })
    } else if (isPrimitive) {
      // For primitive arrays, add an empty string/value
      const safeLocal = Array.isArray(localValue) ? localValue : toArray(localValue)
      updateValue([...safeLocal, ""])
      setTimeout(() => {
        const lastInputIndex = safeLocal.length
        inputRefs.current[lastInputIndex]?.focus()
      }, 0)
    } else {
      // For embedded types
      handleValueChange("")
      setTimeout(() => {
        const lastInputIndex = localValue.length
        inputRefs.current[lastInputIndex]?.focus()
      }, 0)
    }
  }

  const removeInput = (index) => {
    if (isCMSContent) {
      if (localValue.length === 1) {
        setLocalValue([])
        onChange([])
      } else {
        const updatedValues = localValue.filter((_, i) => i !== index)
        setLocalValue(updatedValues)
      }
    } else {
      // For both primitive arrays and embedded types
      const updatedValues = localValue.filter((_, i) => i !== index)
      updateValue(updatedValues)
    }
  }

  const clearAll = () => {
    if (isCMSContent) {
      setLocalValue([])
      onChange([])
    } else {
      updateValue([])
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      addNewInput()
    }
  }

  const handleEdit = (index) => {
    
    if (isCMSContent) {
      const selectedItem = localValue[index]
      if (!selectedItem) return
      setSelectedIndex(index)
      setSelectedInstance(selectedItem)
      setShowDialog(true)
    } else {
      const fieldToEdit = localValue[index] || {}
      
      // If this item has content structure, extract it for the edit dialog
      let editValues = fieldToEdit
      if (fieldToEdit.content && typeof fieldToEdit.content === 'object') {
        editValues = {
          ...fieldToEdit.content,
          _id: fieldToEdit._id,
          // Preserve any other fields that might be needed
          itemLabel: fieldToEdit.itemLabel
        }
      }
      
      setSelectedIndex(index)
      setItemContentValues(editValues)
      setShowDialog(true)
    }
  }

  const loadContent = (index) => {
    if (!isCMSContent && !(isEmbeddedType && field.item.type)) return
    setSelectedIndex(index)
    setShowContentDialog(true)
  }

  const handleCloseDialog = () => {
    setShowDialog(false)
    setSelectedIndex(null)
    setSelectedInstance(null)
    setItemContentValues({})
  }

  const handleSaveItem = async (newValues) => {
    if (isCMSContent) {
      // CMS content save logic
      const updatedValues = [...localValue]
      delete newValues.itemLabel
      delete newValues._id

      const payload = {
        ...selectedInstance,
        content: { type: field.item.type, ...newValues },
      }

      if (payload._id === null) delete payload._id

      const newInstance = await controller.saveContent(payload)
      const _id = newInstance.result

      const updatedItem = {
        ...selectedInstance,
        _id,
        content: newValues,
        itemLabel: previewTitle({ content: newValues }),
      }

      updatedValues[selectedIndex] = updatedItem

      // Handle instance fields
      for (const itemField of field.item.fields) {
        if (!itemField.instance) continue
        const pValues = parentValues()
        pValues.ivalues = pValues.ivalues || {}
        pValues.ivalues[_id] = pValues.ivalues[_id] || {}
        pValues.ivalues[_id][itemField.id] = newValues[itemField.id]
      }

      setLocalValue(updatedValues)
    } else {
      // Embedded type save logic
      const updatedValues = [...localValue]
      const currentItem = updatedValues[selectedIndex] || {}
      
      // If this item has a content structure, update the content properly
      if (currentItem.content && typeof currentItem.content === 'object') {
        updatedValues[selectedIndex] = {
          ...currentItem,
          content: {
            ...currentItem.content,
            ...newValues,
          },
          itemLabel: newValues.text || newValues.name || newValues.title || currentItem.itemLabel
        }
      } else {
        // Simple embedded structure
        updatedValues[selectedIndex] = {
          ...currentItem,
          ...newValues,
        }
      }
      
      updateValue(updatedValues)
    }
    handleCloseDialog()
  }

  // Render helpers
  const renderItem = (item, index) => {
    // Show loading when we're loading and the item doesn't have full content loaded yet
    // For CMS content, we show loading if we don't have the expanded content structure yet
    const hasFullContent = item?.content && typeof item.content === 'object' && item.content.text
    const shouldShowLoading = isLoading && isCMSContent && !hasFullContent
    
    return (
    <div
      key={index}
      className="mt-2 gap-2 flex flex-row items-center w-full"
      draggable={localValue?.length > 1}
      onDragStart={localValue?.length > 1 ? () => handleDragStart(index) : undefined}
      onDragOver={localValue?.length > 1 ? (e) => {
        e.preventDefault()
        handleDragOver(index)
      } : undefined}
      onDragEnd={localValue?.length > 1 ? handleDragEnd : undefined}
    >
      {localValue.length > 1 && (
        <Bars3Icon className="w-5 h-5 cursor-move text-admin_text_secondary mr-2" />
      )}

      <Input
        variant="admin"
        ref={(el) => (inputRefs.current[index] = el)}
        multiline={field.multiline}
        labelClassName="text-admin_text"
        className="flex-grow text-admin_text"
        showLoading={shouldShowLoading}
        placeholder={shouldShowLoading ? "Loading..." : ""}
        value={
          isPrimitive
            ? (typeof item === 'string' ? item : String(item || ""))
            : isCMSContent
              ? (item?.itemLabel || "")
              : (item?.[labelField] || item?.content?.text || item?.content?.name || item?.content?.title || "")
        }
        onChange={(e) => handleValueChange(e.target.value, index)}
        onKeyDown={handleKeyDown}
        onBlur={() => autoResolveFields(index)}
      />

      {!isPrimitive && (
        <Button
          variant="admin_icon"
          className="text-admin_text ml-5"
          onClick={() => handleEdit(index)}
        >
          <PencilIcon className="mx-auto w-6 h-6" />
        </Button>
      )}

      {(isCMSContent || (isEmbeddedType && field.item?.type)) && !isPrimitive && (
        <Button
          variant="admin_icon"
          className="text-admin_text"
          onClick={() => loadContent(index)}
        >
          <FolderIcon className="w-6 h-6 mx-auto" />
        </Button>
      )}

      {showItemDelete && (
        <Button
          variant="admin_icon"
          className="text-admin_text hover:bg-admin_error hover:text-white rounded-full p-2 transition-colors"
          onClick={() => removeInput(index)}
          title="Remove this item"
        >
          <XMarkIcon className="w-6 h-6" />
        </Button>
      )}
    </div>
    )
  }

  // CMS content helpers
  const resolveContentType = (instance) => {
    
    if (!isCMSContent) return field.item
    
    // Special case: Use appropriate schema for content type configuration arrays
    const parentValuesObj = parentValues ? parentValues() : {};
    
    // When editing the fields array of any content type, use FieldType schema
    if (field.id === 'fields' && parentValuesObj?.type) {
      const fieldTypeSchema = getContentType('FieldType');
      return fieldTypeSchema;
    }
    
    // When editing the groups array of any content type, use Group schema  
    if (field.id === 'groups' && parentValuesObj?.type) {
      const groupTypeSchema = getContentType('Group');
      return groupTypeSchema;
    }
    
    // When editing the editors array of any content type, use Editor schema
    if (field.id === 'editors' && parentValuesObj?.type) {
      const editorTypeSchema = getContentType('Editor');
      return editorTypeSchema;
    }
    
    // For Content fields, use the field's referenceType or item type
    if (fieldType === "Content") {
      if (field.referenceType) return getContentType(field.referenceType)
      if (field.item?.type) return field.item
      if (field.item && typeof field.item === 'string') return getContentType(field.item)
    }
    
    // For Array fields with Reference itemType, use referenceType
    if (fieldType === "Array" && field.itemType === "Reference") {
      if (field.referenceType) return getContentType(field.referenceType)
    }

    // For Array fields that contain CMS references, get the type from the content or field definition
    const typeName =
      instance?.content?.type ||
      instance?.type?.value ||
      instance?.type ||
      field.referenceType ||
      field.item?.value ||
      field.item;
      
    
    // Handle case where typeName is already a complete contentType object
    if (typeName && typeof typeName === 'object' && typeName.type && typeName.fields) return typeName
    
    // Handle case where we need to extract the type string from an object
    const typeString = typeof typeName === 'object' ? typeName?.type : typeName;
    
    const resolvedType = getContentType(typeString);
    
    // If getContentType returns undefined, but we have a valid field.item, use it directly
    if (!resolvedType && field.item && typeof field.item === 'object' && field.item.type) return field.item
    
    return resolvedType;
  }

  const getFieldValues = (instance) => {
    if (!isCMSContent) return instance
    
    if (instance?.content) return { ...instance.content, _id: instance._id }

    // Handle null/undefined instance
    if (!instance || typeof instance !== 'object') return instance

    if (Object.keys(instance).includes("name") && !instance.name)
      instance.name = instance.itemLabel

    return instance
  }

  const validators = (errors) => ({
    type: (_, contentType) => {
      if (
        (contentType.type == "Array" || contentType.type == "Content") &&
        !getContentType(contentType.item)
      ) {
        errors.item = "Missing or invalid content type"
      }
    },
  })

  // Memoize field values to prevent unnecessary re-renders
  const memoizedFieldValues = useMemo(() => {
    const result = isCMSContent ? getFieldValues(selectedInstance) : itemContentValues;
    return result;
  }, [isCMSContent, selectedInstance, itemContentValues])

  // Filter items based on search query with CSV support (OR logic)
  const filteredItems = useMemo(() => {
    const items = Array.isArray(localValue) ? localValue : []
    if (!searchQuery.trim()) return items

    // Split by comma and trim each term
    const searchTerms = searchQuery.split(',').map(term => term.trim().toLowerCase()).filter(term => term.length > 0)

    return items.filter((item) => {
      // Get all possible searchable fields from the item
      let searchableText = ""

      if (isPrimitive) {
        searchableText = (typeof item === 'string' ? item : String(item || "")).toLowerCase()
      } else if (isCMSContent) {
        searchableText = (item?.itemLabel || "").toLowerCase()
      } else {
        // For embedded types, collect all text fields
        const textFields = [
          item?.[labelField],
          item?.content?.text,
          item?.content?.name,
          item?.content?.title,
          item?.content?.label,
          item?.content?.id,
          item?.id,
          item?.name,
          item?.label,
          item?.text
        ].filter(Boolean).join(' ').toLowerCase()

        searchableText = textFields
      }

      // OR logic: match if ANY search term is found
      return searchTerms.some(term => searchableText.includes(term))
    })
  }, [localValue, searchQuery, isPrimitive, isCMSContent, labelField])

  return (
    <div className="array-editor">
      {localValue?.length > 10 && (
        <div className="flex items-center justify-between mb-4">
          <Input
            variant="admin"
            className="w-64 text-admin_text"
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            showClearButton={true}
            clearButtonClassName="text-admin_text_secondary hover:text-admin_primary"
          />

          <span className="mr-10 text-sm text-admin_text">
            {filteredItems?.length || 0} {(filteredItems?.length || 0) === 1 ? "item" : "items"}
            {searchQuery && ` (${localValue?.length || 0} total)`}
          </span>
        </div>
      )}

      {filteredItems?.map((item, index) => {
        // Find the original index in localValue
        const originalIndex = localValue?.indexOf(item)
        return renderItem(item, originalIndex)
      })}

      <div className="flex flex-row justify-end items-center mt-4">
        <div className="flex items-center gap-4">
          {showAddBtn && (field.multiple !== false || !localValue?.length) && (
            <Button
              variant="admin_secondary"
              className="whitespace-nowrap"
              onClick={addNewInput}
            >
              Add Item
            </Button>
          )}

          {showClearBtn && localValue?.length > 0 && (
            <Button
              variant="admin_secondary"
              className="whitespace-nowrap"
              onClick={clearAll}
            >
              Clear All
            </Button>
          )}
        </div>
      </div>

      {/* Content Selector Dialog */}
      {(isCMSContent || (isEmbeddedType && field.item.type)) && showContentDialog && (
        <ContentSelectorDialog
          parentValues={parentValues}
          type={field.referenceType || field.item?.type || field.item}
          isOpen={showContentDialog}
          onClose={() => setShowContentDialog(false)}
          field={field}
          existingItems={localValue || []}
          onSelect={(item) => {
            const updatedValues = [...localValue]
            if (isCMSContent) {
              updatedValues[selectedIndex] = {
                _id: item._id,
                content: item.content,
                itemLabel:
                  item.content.name ||
                  item.content.title ||
                  item.content.label ||
                  item.content.id ||
                  "",
              }
              setLocalValue(updatedValues)
            } else if (isEmbeddedType && field.item.type) {
              // For embedded types, copy the content directly
              updatedValues[selectedIndex] = {
                ...item.content,
                _id: item._id,
                [labelField]: item.content.name || item.content.title || item.content.label || "",
              }
              updateValue(updatedValues)
            }
          }}
        />
      )}

      {/* Edit Dialog */}
      {showDialog && (
        <Modal
          isOpen={showDialog}
          onClose={handleCloseDialog}
          closeOnEscape
          variant={isCMSContent ? "admin" : "admin_compact"}
          className={isCMSContent ? 
            "w-full sm:w-[500px] md:w-[80%] md:absolute md:top-[65px] h-full md:h-auto pb-10" :
            undefined
          }
        >
          <ContentEditor
            enableFieldSettings={isCMSContent ? !!selectedInstance?.content : false}
            dataItem={isCMSContent ? selectedInstance : (selectedInstance || itemContentValues)}
            title={`Edit ${
              isCMSContent 
                ? (selectedInstance?.itemLabel ?? "Item")
                : (itemContentValues.label ?? "Item")
            }`}
            contentType={resolveContentType(selectedInstance)}
            parentValues={parentValues}
            fieldValues={memoizedFieldValues}
            validators={isEmbeddedType ? validators : undefined}
            onSave={handleSaveItem}
            onClickSave={handleCloseDialog}
            onError={(errors) => {
              showNotification({
                message: Object.values(errors).find((e) => e),
                className: "bg-admin_error text-white",
              })
            }}
          />
        </Modal>
      )}
    </div>
  )
}